'use client'

import { useState, useEffect, useRef } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useAuth } from '@/context/AuthContext'
import { useWorkspace } from '@/context/WorkspaceContext'
import { ChevronDownIcon, BellIcon, PlusIcon } from '@heroicons/react/24/outline'

export default function Header() {
  const router = useRouter()
  const { isAuthenticated, user, logout } = useAuth()
  const { currentWorkspace, availableWorkspaces, setCurrentWorkspace } = useWorkspace()
  const [isScrolled, setIsScrolled] = useState(false)
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false)
  const [isWorkspaceMenuOpen, setIsWorkspaceMenuOpen] = useState(false)
  
  const profileMenuRef = useRef<HTMLDivElement>(null)
  const workspaceMenuRef = useRef<HTMLDivElement>(null)
  
  // Handle scrolling effect for header
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true)
      } else {
        setIsScrolled(false)
      }
    }
    
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])
  
  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setIsProfileMenuOpen(false)
      }
      if (workspaceMenuRef.current && !workspaceMenuRef.current.contains(event.target as Node)) {
        setIsWorkspaceMenuOpen(false)
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])
  
  const handleLogout = async () => {
    setIsProfileMenuOpen(false)
    router.push('/login')
    
    setTimeout(() => {
      logout()
    }, 100)
  }

  const handleWorkspaceChange = (workspaceCode: string) => {
    setCurrentWorkspace(workspaceCode)
    setIsWorkspaceMenuOpen(false)
  }

  // Get current workspace display name
  const getCurrentWorkspaceName = () => {
    if (currentWorkspace === 'APP_PUBLIC') return 'Public Workspace'
    const workspace = availableWorkspaces.find(w => w.code === currentWorkspace)
    return workspace?.name || currentWorkspace
  }
  
  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-[#0c0c1d]/80 backdrop-blur-xl shadow-lg' : 'bg-transparent'}`}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Left Group: Logo */}
          <div className="flex items-center">
            <Link href={isAuthenticated ? '/dashboard' : '/'} className="flex items-center space-x-3">
              <Image 
                src="/logo.png" 
                alt="AI Learning Platform" 
                width={40} 
                height={40} 
                className="w-8 h-8 md:w-10 md:h-10"
              />
              <span className="font-bold text-xl md:text-2xl text-white">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">AI</span>
                <span> Learning Platform</span>
              </span>
            </Link>
          </div>

          {/* Center Group: Navigation Buttons */}
          {isAuthenticated && user?.permissions?.some(p => p === 'admin' || p === 'superadmin') && (
            <div className="hidden md:flex items-center space-x-4">
              <Link
                href="/notifications"
                className="flex items-center space-x-2 px-6 py-2.5 text-sm font-medium text-white 
                           hover:text-cyan-300
                           rounded-xl transition-all duration-200 ease-in-out
                           transform hover:scale-[1.02]"
              >
                <BellIcon className="w-4 h-4" />
                <span>Notifications</span>
              </Link>
            </div>
          )}
          
          {/* Right Group: Workspace Selector + Profile Dropdown */}
          <div className="flex items-center space-x-4">
            {/* Workspace Selector - only show for superadmin users */}
            {isAuthenticated && user?.permissions?.includes('superadmin') && availableWorkspaces.length > 0 && (
              <div className="relative" ref={workspaceMenuRef}>
                <button
                  onClick={() => setIsWorkspaceMenuOpen(!isWorkspaceMenuOpen)}
                  className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-colors text-sm"
                >
                  <svg className="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <span className="hidden md:block text-white">{getCurrentWorkspaceName()}</span>
                  <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                </button>
                
                {isWorkspaceMenuOpen && (
                  <div className="absolute right-0 mt-2 w-64 rounded-lg shadow-lg bg-[#0c0c1d] border border-white/10 py-1 z-10">
                    <div className="px-4 py-2 border-b border-white/10">
                      <p className="text-xs text-gray-400 uppercase tracking-wider">Select Workspace</p>
                    </div>
                    
                    {/* APP_PUBLIC option */}
                    <button
                      onClick={() => handleWorkspaceChange('APP_PUBLIC')}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-white/5 flex items-center space-x-2 ${
                        currentWorkspace === 'APP_PUBLIC' ? 'text-cyan-400 bg-white/5' : 'text-white'
                      }`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Public Workspace</span>
                      {currentWorkspace === 'APP_PUBLIC' && (
                        <svg className="w-4 h-4 ml-auto" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                    
                    {/* User workspaces */}
                    {availableWorkspaces.map((workspace) => (
                      <button
                        key={workspace.code}
                        onClick={() => handleWorkspaceChange(workspace.code)}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-white/5 flex items-center space-x-2 ${
                          currentWorkspace === workspace.code ? 'text-cyan-400 bg-white/5' : 'text-white'
                        }`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        <div className="flex-1">
                          <div className="font-medium">{workspace.name}</div>
                          <div className="text-xs text-gray-400">{workspace.code}</div>
                        </div>
                        {currentWorkspace === workspace.code && (
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Profile Dropdown */}
            {isAuthenticated ? (
              <div className="relative" ref={profileMenuRef}>
                <button
                  onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                  className="flex items-center space-x-3 p-2 rounded-lg hover:bg-white/5 transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-cyan-400 to-blue-400 flex items-center justify-center">
                    {user?.picture_url ? (
                      <Image 
                        src={user.picture_url} 
                        alt="Profile" 
                        width={32}
                        height={32}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <span className="text-white text-sm font-medium">
                        {user?.first_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                      </span>
                    )}
                  </div>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium text-white">
                      {user?.first_name && user?.last_name 
                        ? `${user.first_name} ${user.last_name}`
                        : user?.first_name 
                        ? user.first_name
                        : user?.email?.split('@')[0] || 'User'}
                    </p>
                    <p className="text-xs text-gray-400">
                      {user?.email || ''}
                    </p>
                  </div>
                  <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                </button>
                
                {isProfileMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 rounded-lg shadow-lg bg-[#0c0c1d] border border-white/10 py-1 z-10">
                    <div className="px-4 py-3 border-b border-white/10">
                      <p className="text-sm text-white font-medium truncate">
                        {user?.first_name && user?.last_name 
                          ? `${user.first_name} ${user.last_name}`
                          : user?.first_name 
                          ? user.first_name
                          : user?.email?.split('@')[0] || 'User'}
                      </p>
                      <p className="text-xs text-gray-400 truncate">
                        {user?.email || ''}
                      </p>
                    </div>
                    
                    {/* Content Management options for admin and superadmin */}
                    {user?.permissions?.some(p => p === 'admin' || p === 'superadmin') && (
                      <div className="border-b border-white/10 py-1">
                        <Link
                          href="/content-generation"
                          onClick={() => setIsProfileMenuOpen(false)}
                          className="block px-4 py-2 text-sm text-cyan-400 hover:bg-white/5 hover:text-cyan-300 flex items-center space-x-2"
                        >
                          <PlusIcon className="w-4 h-4" />
                          <span>Content Generation</span>
                        </Link>
                        <Link
                          href="/content-management"
                          onClick={() => setIsProfileMenuOpen(false)}
                          className="block px-4 py-2 text-sm text-cyan-400 hover:bg-white/5 hover:text-cyan-300 flex items-center space-x-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span>Content Management</span>
                        </Link>
                      </div>
                    )}

                    {/* Developer and Playground options for superadmin */}
                    {user?.permissions?.some(p => p === 'superadmin') && (
                      <div className="border-b border-white/10 py-1">
                        <Link
                          href="/developer"
                          onClick={() => setIsProfileMenuOpen(false)}
                          className="block px-4 py-2 text-sm text-cyan-400 hover:bg-white/5 hover:text-cyan-300 flex items-center space-x-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                          </svg>
                          <span>Developer</span>
                        </Link>
                        <Link
                          href="/playground"
                          onClick={() => setIsProfileMenuOpen(false)}
                          className="block px-4 py-2 text-sm text-cyan-400 hover:bg-white/5 hover:text-cyan-300 flex items-center space-x-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          <span>Playground</span>
                        </Link>
                        <Link
                          href="/onboarding"
                          onClick={() => setIsProfileMenuOpen(false)}
                          className="block px-4 py-2 text-sm text-cyan-400 hover:bg-white/5 hover:text-cyan-300 flex items-center space-x-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                          </svg>
                          <span>Onboarding</span>
                        </Link>
                        <Link
                          href="/pricing"
                          onClick={() => setIsProfileMenuOpen(false)}
                          className="block px-4 py-2 text-sm text-cyan-400 hover:bg-white/5 hover:text-cyan-300 flex items-center space-x-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                          </svg>
                          <span>Pricing Management</span>
                        </Link>
                        <Link
                          href="/workspace-management"
                          onClick={() => setIsProfileMenuOpen(false)}
                          className="block px-4 py-2 text-sm text-cyan-400 hover:bg-white/5 hover:text-cyan-300 flex items-center space-x-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                          <span>Workspace Management</span>
                        </Link>
                        <Link
                          href="/automation"
                          onClick={() => setIsProfileMenuOpen(false)}
                          className="block px-4 py-2 text-sm text-cyan-400 hover:bg-white/5 hover:text-cyan-300 flex items-center space-x-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          <span>Automation</span>
                        </Link>
                        <Link
                          href="/partners-admin"
                          onClick={() => setIsProfileMenuOpen(false)}
                          className="block px-4 py-2 text-sm text-cyan-400 hover:bg-white/5 hover:text-cyan-300 flex items-center space-x-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          <span>Partners Admin</span>
                        </Link>
                      </div>
                    )}
                    
                    
                    <div className="border-t border-white/10 mt-1 pt-1">
                      <button 
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-white/5 hover:text-red-300"
                      >
                        Sign out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-4">
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  )
} 